import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    titleText = "titleText",
    bodyTextLines = "bodyTextLines",
    imageUrl = "imageUrl",
    buttonText = "buttonText",
    buttonLink = "buttonLink",
    customCSS = "customCSS",
}

interface BodyTextLineModel {
    fields: {
        line: string;
    };
}

interface OwnProps {
    extendedStyles: WidgetCharlieStyles;
    fields: {
        [fields.titleText]: string;
        [fields.bodyTextLines]: BodyTextLineModel[];
        [fields.imageUrl]: string;
        [fields.buttonText]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.customCSS]: string;
    };
}

type Props = OwnProps;

export interface WidgetCharlieStyles {
    wrapper?: InjectableCss;
    mainPanel?: InjectableCss;
    leftPanel?: InjectableCss;
    imagePanel?: InjectableCss;
    image?: LazyImageProps;
    rightPanel?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    bodyWrapper?: InjectableCss;
    bodyLineWrapper?: InjectableCss;
    bodyLineText?: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
    link?: LinkPresentationProps;
}

export const widgetCharlieStyles: WidgetCharlieStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    mainPanel: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: row;
            justify-content: start;
            align-items: center;
        `,
    },
    leftPanel: {
        css: css`
            display: flex;
        `,
    },
    imagePanel: {
        css: css`
            display: flex;
        `,
    },
    image: {
        css: css`
            height: 175px;
            width: 175px;
            padding: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        height: 100px;
                        width: 100px;
                        padding: 10px;
                    `,
                    css`
                        height: 100px;
                        width: 100px;
                        padding: 10px;
                    `,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    rightPanel: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    titleWrapper: {
        css: css`
            width: 100%;
            padding-bottom: 4px;
        `,
    },
    title: {
        variant: "h3",
        transform: "inherit",
    },
    bodyWrapper: {
        css: css`
            width: 100%;
            padding-bottom: 1em;
        `,
    },
    bodyLineWrapper: {
        css: css`
            width: 100%;
        `,
    },
    bodyLineText: {
        css: css`
            width: 100%;
            font-size: 20px;
            line-height: 27px;
        `,
    },
    linkWrapper: {
        css: css`
            width: 100%;
            color: black;
        `,
    },
    link: {
        css: css`
            width: 100%;
        `,
        typographyProps: {
            css: css`
                font-weight: 500;
                font-size: 20px;
                line-height: 25px;
                color: black;
            `,
        },
    },
};

const WidgetCharlie: FC<Props> = props => {
    const { extendedStyles, fields } = props;
    const { titleText, bodyTextLines, imageUrl, buttonText, buttonLink, customCSS } = fields;

    const [styles] = React.useState(() => mergeToNew(widgetCharlieStyles, extendedStyles));

    const { url } = useGetLink(buttonLink);

    const customCssWrapper = {
        css: css`
            ${customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper} data-test-selector="widgetCharlie_customCssWrapper">
            <StyledWrapper {...styles.wrapper} className="wrapper" data-test-selector="wrapper">
                <StyledWrapper {...styles.mainPanel} data-test-selector="mainPanel">
                    <StyledWrapper {...styles.leftPanel} data-test-selector="leftPanel">
                        <StyledWrapper {...styles.imagePanel} data-test-selector="imagePanel">
                            <LazyImage src={imageUrl} {...styles.image} />
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.rightPanel} data-test-selector="rightPanel">
                        <StyledWrapper {...styles.titleWrapper} data-test-selector="titleWrapper">
                            <Typography {...styles.title}>{titleText}</Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.bodyWrapper} data-test-selector="bodyWrapper">
                            {bodyTextLines &&
                                bodyTextLines.map(item => (
                                    <StyledWrapper {...styles.bodyLineWrapper} key={item.fields.line}>
                                        <Typography {...styles.bodyLineText}>{item.fields.line}</Typography>
                                    </StyledWrapper>
                                ))}
                        </StyledWrapper>
                        <StyledWrapper {...styles.linkWrapper} data-test-selector="linkWrapper">
                            <Link {...styles.link} href={url}>
                                {buttonText}
                            </Link>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.wrapper {

}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 1,
};

const buttonTab = {
    displayName: "Button",
    sortOrder: 2,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 3,
};

const widgetModule: WidgetModule = {
    component: WidgetCharlie,
    definition: {
        group: "Basic",
        displayName: "Widget Charlie",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.titleText,
                displayName: "Title",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                tooltip: "",
                tab: basicTab,
                sortOrder: 10,
            },
            {
                name: fields.bodyTextLines,
                displayName: "Body Text",
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: HasFields) => {
                    return item.fields.line;
                },
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "line",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
                tab: basicTab,
                sortOrder: 20,
            },
            {
                name: fields.imageUrl,
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isRequired: true,
                fieldType: "Translatable",
                tab: basicTab,
                sortOrder: 30,
            },
            {
                name: fields.buttonText,
                displayName: "Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                tooltip: "",
                tab: buttonTab,
                sortOrder: 110,
            },
            {
                name: fields.buttonLink,
                fieldType: "General",
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: buttonTab,
                sortOrder: 120,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                sortOrder: 200,
                defaultValue: defaultCustomCss,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
